import React, { useState, useEffect } from 'react';
import api from '../api';

const PLAN_COLORS = { trial: '#f59e0b', starter: '#4f46e5', growth: '#06b6d4', business: '#10b981' };
const PLAN_ICONS = { trial: '⏳', starter: '🚀', growth: '📈', business: '🏢' };

export default function Billing() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState('');
  const [portalLoading, setPortalLoading] = useState(false);

  useEffect(() => {
    api.get('/billing').then(r => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const startCheckout = async (plan) => {
    setCheckoutLoading(plan);
    try {
      const res = await api.post('/billing/checkout', { plan }).then(r => r.data);
      window.location.href = res.url;
    } catch (e) {
      alert(e.response?.data?.error || 'Checkout failed');
    }
    setCheckoutLoading('');
  };

  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const res = await api.post('/billing/portal').then(r => r.data);
      window.location.href = res.url;
    } catch (e) {
      alert(e.response?.data?.error || 'Could not open billing portal');
    }
    setPortalLoading(false);
  };

  if (loading) return <div style={{ padding: 40, color: 'var(--muted)' }}>Loading billing…</div>;

  const { workspace, plans, employee_count } = data || {};
  const currentPlan = workspace?.plan || 'trial';
  const seatsUsed = employee_count || 0;
  const seatsLimit = workspace?.seats_limit;
  const trialDaysLeft = workspace?.trial_ends_at
    ? Math.max(0, Math.ceil((new Date(workspace.trial_ends_at) - new Date()) / 86400000))
    : 0;
  const isActive = workspace?.subscription_status === 'active';

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '32px 24px' }}>
      <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1e293b', marginBottom: 8 }}>Billing & Plan</h1>
      <p style={{ color: 'var(--muted)', marginBottom: 32 }}>Manage your subscription and billing details.</p>

      {/* Current Plan */}
      <div style={{ background: 'var(--glass)', backdropFilter: 'var(--blur)', WebkitBackdropFilter: 'var(--blur)', borderRadius: 12, border: '1px solid var(--glass-border)', padding: 24, marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
              <span style={{ fontSize: '1.5rem' }}>{PLAN_ICONS[currentPlan] || '📦'}</span>
              <span style={{ fontSize: '1.2rem', fontWeight: 700, color: '#1e293b', textTransform: 'capitalize' }}>{currentPlan} Plan</span>
              <span style={{ background: PLAN_COLORS[currentPlan] + '20', color: PLAN_COLORS[currentPlan], padding: '2px 10px', borderRadius: 99, fontSize: '0.75rem', fontWeight: 600 }}>
                {workspace?.subscription_status || 'trial'}
              </span>
            </div>
            {currentPlan === 'trial' && (
              <p style={{ color: '#f59e0b', fontSize: '0.875rem' }}>
                {trialDaysLeft > 0 ? `${trialDaysLeft} days remaining in your trial` : 'Trial expired — upgrade to continue'}
              </p>
            )}
            <p style={{ color: 'var(--muted)', fontSize: '0.875rem' }}>
              Seats: <strong>{seatsUsed}</strong> used of <strong>{seatsLimit ?? '∞'}</strong>
            </p>
          </div>
          {isActive && (
            <button onClick={openPortal} disabled={portalLoading} className="btn" style={{ padding: '8px 18px', background: 'var(--hairline)', color: '#475569', border: '1px solid var(--glass-border)' }}>
              {portalLoading ? 'Loading…' : 'Manage Billing →'}
            </button>
          )}
        </div>

        {/* Seat usage bar */}
        {seatsLimit && (
          <div style={{ marginTop: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: 'var(--muted)', marginBottom: 6 }}>
              <span>Seat usage</span><span>{seatsUsed}/{seatsLimit}</span>
            </div>
            <div style={{ background: 'var(--hairline)', borderRadius: 99, height: 8 }}>
              <div style={{ width: `${Math.min(100, (seatsUsed / seatsLimit) * 100)}%`, height: '100%', borderRadius: 99, background: seatsUsed >= seatsLimit ? '#ef4444' : '#4f46e5', transition: 'width .3s' }} />
            </div>
          </div>
        )}
      </div>

      {/* Plans */}
      <h2 style={{ fontSize: '1.1rem', fontWeight: 600, color: '#1e293b', marginBottom: 16 }}>
        {isActive ? 'Change plan' : 'Choose a plan'}
      </h2>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16, marginBottom: 32 }}>
        {plans && Object.entries(plans).map(([key, plan]) => {
          const isCurrent = currentPlan === key && isActive;
          return (
            <div key={key} style={{ background: 'var(--glass)', backdropFilter: 'var(--blur)', WebkitBackdropFilter: 'var(--blur)', borderRadius: 12, border: `2px solid ${isCurrent ? '#4f46e5' : 'var(--glass-border)'}`, padding: 24, position: 'relative' }}>
              {isCurrent && (
                <div style={{ position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)', background: '#4f46e5', color: '#fff', padding: '2px 14px', borderRadius: 99, fontSize: '0.75rem', fontWeight: 700 }}>Current</div>
              )}
              {key === 'growth' && !isCurrent && (
                <div style={{ position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)', background: '#06b6d4', color: '#fff', padding: '2px 14px', borderRadius: 99, fontSize: '0.75rem', fontWeight: 700 }}>Popular</div>
              )}
              <div style={{ fontSize: '1.4rem', marginBottom: 8 }}>{PLAN_ICONS[key] || '📦'}</div>
              <h3 style={{ fontWeight: 700, color: '#1e293b', marginBottom: 4 }}>{plan.name}</h3>
              <div style={{ fontSize: '1.8rem', fontWeight: 800, color: '#1e293b', marginBottom: 4 }}>
                ${plan.price}<span style={{ fontSize: '0.9rem', fontWeight: 400, color: 'var(--muted)' }}>/mo</span>
              </div>
              <p style={{ color: 'var(--muted)', fontSize: '0.875rem', marginBottom: 20 }}>
                {plan.seats ? `Up to ${plan.seats} employees` : 'Unlimited employees'}
              </p>
              <button
                onClick={() => startCheckout(key)}
                disabled={isCurrent || checkoutLoading === key}
                className="btn btn-primary"
                style={{ width: '100%', justifyContent: 'center', padding: '10px', background: isCurrent ? 'var(--glass-border)' : '#4f46e5', color: isCurrent ? 'var(--muted)' : '#fff', cursor: isCurrent ? 'default' : 'pointer' }}>
                {checkoutLoading === key ? 'Loading…' : isCurrent ? 'Current plan' : isActive ? 'Switch' : 'Subscribe'}
              </button>
            </div>
          );
        })}
      </div>

      <p style={{ color: 'var(--muted)', fontSize: '0.8rem' }}>
        All plans include: time tracking, screenshots, activity monitoring, reports, and team management.
        Subscriptions are billed monthly and can be cancelled anytime.
      </p>
    </div>
  );
}
